import {
  JSONEditor,
  JupiterTheme,
  parseAsJson,
} from '@jupiterone/jupiter-web-toolkit';
import { IconButton, InputLabel, Typography, Tooltip } from '@material-ui/core';
import HelpIcon from 'react-feather/dist/icons/help-circle';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'react-feather';
import { useBooleanState } from 'react-use-object-state';
import { useDebouncedCallback } from 'use-debounce';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    container: {
      height: 350,
      marginBottom: theme.spacing(7.5),
      transition: 'height 0.15s ease-out',
    },
    shrunkenContainer: { height: 64 },
    error: { color: theme.palette.red[500] },
    iconButton: {
      width: '100%',
      color: theme.palette.coolGrey[400],
      transition: '0.25s',
      '&:hover': {
        color: theme.palette.primary.main,
      },
    },
  })
);

interface Props {
  value: object;
  label?: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  name: string;
  disabled?: boolean;
  helpText?: string;
}

const JSONInput = ({
  value,
  label,
  onChange,
  name,
  disabled,
  helpText,
}: Props) => {
  const classes = useStyles();
  const [internalValue, setInternalValue] = useState<string>('');
  const hasError = useBooleanState(false);
  const expanded = useBooleanState(true);

  useEffect(() => {
    setInternalValue(JSON.stringify(value, null, 2));
    // Ace has a bug where it doesn't resize properly. This starts it out at max height and then shrinks it down
    setTimeout(expanded.setFalse, 1);
  }, []);

  const handleJSONChange = (value: string) => {
    const parsed = parseAsJson(value);
    if (parsed) {
      onChange({ target: { name, value: parsed } } as any);
    }
  };

  const [validateAndUpdateJSON] = useDebouncedCallback(() => {
    const result = parseAsJson(internalValue);
    if (result) {
      handleJSONChange(internalValue);
      hasError.setFalse();
    } else {
      hasError.setTrue();
    }
  }, 500);

  useEffect(validateAndUpdateJSON, [internalValue]);

  return (
    <div
      className={clsx(
        classes.container,
        expanded.state === false && classes.shrunkenContainer
      )}
    >
      {label && (
        <InputLabel
          shrink={true}
          className={clsx(hasError.state && classes.error)}
        >
          {label}
          {helpText && (
            <Tooltip title={helpText}>
              <IconButton size={'small'}>
                <HelpIcon size={16} />
              </IconButton>
            </Tooltip>
          )}
        </InputLabel>
      )}
      <JSONEditor
        value={internalValue}
        onChange={setInternalValue}
        readOnly={disabled}
        wrap={true}
      />
      {hasError.state && (
        <Typography variant="body2" className={classes.error}>
          Invalid JSON
        </Typography>
      )}
      <IconButton className={classes.iconButton} onClick={expanded.toggle}>
        {expanded.state ? <ChevronUp /> : <ChevronDown />}
      </IconButton>
    </div>
  );
};

export default JSONInput;
